import { CHAT_MODE_ICON, CHAT_MODE_LABEL, type ChatAttachment, type ChatMode, type ChatThread, type CreatableChatMode } from '@naia-app/chat-client';

import { api } from './api';
import { BACKEND_URL } from './config';
import { requireSession } from './supabase';

/* CUTOVER (Vague 3) : chat tuteur étudiant sur le backend apps/api (Vertex) au */
/* lieu de la fn edge chat-respond. SSE + pièces jointes images supportées. */
const CHAT_URL = `${BACKEND_URL}/chat`;
import { dispatchChatSseEvent } from './chat-sse-dispatch';
import { drainSseStream, assertSseResponse } from './sse';

/* Canonical contract shared with mobile + backend (deduplicated, Phase 4). */
/* Re-exported for the web's existing import sites. ChatMessage stays local */
/* because the web carries a typed metadata.tools (ToolCall) the canonical */
/* contract leaves as unknown[]. */
export { CHAT_MODE_ICON, CHAT_MODE_LABEL };
export type { ChatAttachment, ChatMode, ChatThread, CreatableChatMode };

export interface ChatMessage {
  id: string;
  thread_id: string | null;
  role: 'user' | 'assistant';
  transcript: string;
  attachments: ChatAttachment[] | null;
  /* Curriculum tool calls of the turn (cours mode) — same shape as the */
  /* teacher chat's ToolCall, persisted server-side for chip replay. */
  metadata?: { tools?: import('./editor-chat').ToolCall[]; clientSendId?: string } | null;
  turn_failed?: boolean;
  created_at: string;
}

export async function listThreads(limit = 30, excludeMode?: ChatMode, includeTestData?: boolean): Promise<ChatThread[]> {
  /* CLEAN-ARCH (Vague 4) : via le port tRPC typé (api.threads.list) → backend */
  /* (scopé au caller server-side), au lieu d'un appel supabase-js direct. */
  return (await api.threads.list.query({ limit, excludeMode, includeTestData })) as ChatThread[];
}

export async function getThread(id: string): Promise<ChatThread | null> {
  /* CLEAN-ARCH (Vague 4) : via le port tRPC (ownership server-side). */
  return (await api.threads.get.query({ threadId: id })) as ChatThread | null;
}

export async function renameThread(id: string, title: string): Promise<void> {
  const t = title.trim();
  if (!t) return;
  /* CLEAN-ARCH (Vague 4) : via le port tRPC (ownership-scoped server-side). */
  await api.threads.rename.mutate({ threadId: id, title: t });
}

export async function createThread(input: {
  mode: CreatableChatMode;
  parcoursId?: string | null;
  title?: string;
}): Promise<ChatThread> {
  /* CLEAN-ARCH (Vague 4) : via le port tRPC typé (api.threads.create) → backend. */
  /* Le titre par défaut (libellés i18n) reste calculé côté client. */
  const created = await api.threads.create.mutate({
    mode: input.mode,
    parcoursId: input.parcoursId ?? null,
    title: input.title ?? defaultTitleForMode(input.mode, input.parcoursId),
  });
  return created as ChatThread;
}

export async function listMessages(threadId: string, limit = 100): Promise<ChatMessage[]> {
  /* CLEAN-ARCH (Vague 4) : via le port tRPC (ownership server-side). */
  return (await api.messages.list.query({ threadId, limit })) as ChatMessage[];
}

export async function deleteThread(id: string): Promise<void> {
  /* CLEAN-ARCH (Vague 4) : via le port tRPC (ownership-scoped server-side). */
  await api.threads.delete.mutate({ threadId: id });
}

export interface SendMessageInput {
  threadId: string;
  text: string;
  mode: ChatMode;
  parcoursId?: string | null;
  history: { role: 'user' | 'assistant'; transcript: string }[];
  attachments?: ChatAttachment[];
  clientSendId?: string;
  /* Round-trip: set when the user answered an interactive widget (e.g. ask_mcq). */
  /* The Edge function replays the pending call + result and continues the turn. */
  resume?: { name: string; args: unknown; result: unknown };
}

/** A widget the model asked to render, as forwarded over the `widget` SSE event. */
export interface WidgetEvent {
  /* Widget contract names + the dedicated cours-workflow tools (server-rendered */
  /* canonical templates: cours_intent, cours_config, cours_refine). */
  name?: 'render_widget' | 'ask_mcq' | 'render_interactive' | 'ask_form' | `cours_${string}`;
  toolCallId?: string;
  interactive?: boolean;
  args?: unknown;
  html?: string;
  title?: string;
  /* Optional render height (px) — the cours form widgets are taller than the */
  /* 360px default frame. */
  height?: number;
  /* True when emitted by a live voice session — its answer rides the open */
  /* Gemini Live WS as a toolResponse, not a new chat-respond request. */
  voice?: boolean;
}


export interface CourseSectionRow {
  id: string;
  position: number;
  type: string;
  title: string | null;
  content: unknown;
  created_at?: string;
  updated_at?: string;
  document_id?: string;
}

export type SectionUpdatedEvent = CourseSectionRow;
export type SectionCreatedEvent = CourseSectionRow;
export interface SectionsReorderedEvent {
  sections: CourseSectionRow[];
}

/** Enveloppe générique émise par un outil du catalogue tRPC après une écriture
 *  réussie (CHAT_TOOL_RESOURCE_INVALIDATORS côté serveur) — jamais la donnée,
 *  au consommateur de recharger la ressource visée. */
export interface ResourceChangedEvent {
  resource: string;
  id: string;
}

/**
 * Best-effort human-readable label for an interactive widget result (whatever
 * the widget's Naïa.submit(value) / mcq selection passed), so the user's
 * answer can be shown as a chat bubble.
 */
export function readableResult(result: unknown): string | null {
  if (typeof result === 'string') return result.trim() || null;
  if (typeof result === 'number' || typeof result === 'boolean') return String(result);
  if (result && typeof result === 'object') {
    const o = result as Record<string, unknown>;
    for (const k of ['label', 'choix', 'text', 'answer', 'choice', 'title', 'value']) {
      const v = o[k];
      if (typeof v === 'string' && v.trim()) return v.trim();
      if (typeof v === 'number') return String(v);
    }
  }
  return null;
}

export async function uploadAttachment(input: {
  threadId: string;
  file: File;
}): Promise<ChatAttachment> {
  /* CLEAN-ARCH (Vague 4) : URL d'upload signée via le port tRPC, puis PUT direct */
  /* sur le storage (le fichier ne transite jamais par l'API). Pas de supabase ici. */
  const ext = (input.file.name.split('.').pop() ?? 'jpg').toLowerCase();
  const { path, signedUrl } = await api.attachments.createUpload.mutate({ threadId: input.threadId, ext });

  const put = await fetch(signedUrl, {
    method: 'PUT',
    headers: { 'Content-Type': input.file.type || 'image/jpeg' },
    body: input.file,
  });
  if (!put.ok) throw new Error(`upload failed (${put.status})`);

  /* Signed read URL for the in-chat preview (purely a UI URL; the backend */
  /* re-downloads via service role + storagePath when sending the turn). */
  const url = await api.attachments.signRead.mutate({ path });
  return { url, mime: input.file.type || 'image/jpeg', storagePath: path };
}

export async function sendMessageStream(
  input: SendMessageInput,
  callbacks: {
    onDelta: (chunk: string) => void;
    onWidget?: (widget: WidgetEvent) => void;

    onSectionUpdated?: (section: SectionUpdatedEvent) => void;
    onSectionCreated?: (section: SectionCreatedEvent) => void;
    onSectionsReordered?: (event: SectionsReorderedEvent) => void;
    onResourceChanged?: (event: ResourceChangedEvent) => void;
    onTool?: (name: string, args: unknown) => void;
    onToolResult?: (name: string, refs: import('./editor-chat').DocRef[], count?: number, levels?: string[]) => void;
    /* Le serveur retire un tour non fondé et le régénère : sans ce rappel, le
       texte rétracté reste dans le tampon et la réponse s'affiche deux fois à
       l'écran pendant la génération. student-chat et editor-chat le traitent
       déjà — ce client était le seul à l'ignorer. */
    onRetract?: () => void;
    onDone?: (fullText: string) => void;
    onError?: (err: Error) => void;
    onSlow?: () => void;
    onActive?: () => void;
  },
  signal?: AbortSignal,
): Promise<void> {
  const session = await requireSession();

  const body = JSON.stringify({
    threadId: input.threadId,
    userMessage: input.text,
    clientSendId: input.clientSendId,
    mode: input.mode,
    parcoursId: input.parcoursId,
    attachments: input.attachments ?? [],
    messages: input.history.map((m) => ({ role: m.role, content: m.transcript })),
    resume: input.resume,
  });

  const res = await fetch(CHAT_URL, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Accept: 'text/event-stream', Authorization: `Bearer ${session.access_token}` },
    body,
    signal,
  });

  await assertSseResponse(res, 'chat-respond');

  await drainSseStream(res.body!, ({ event, data }) => {
    try {
      dispatchChatSseEvent(event, data, {
        onDelta: (d) => {
          if (d.text) callbacks.onDelta(d.text);
        },
        onWidget: (d) => {
          const w = d as WidgetEvent;
          const hasContent = w.name === 'render_widget' ? !!w.html?.trim() : !!(w.html || w.name);
          if (hasContent) callbacks.onWidget?.(w);
        },
        onSectionUpdated: (d) => callbacks.onSectionUpdated?.(d as SectionUpdatedEvent),
        onSectionCreated: (d) => callbacks.onSectionCreated?.(d as SectionCreatedEvent),
        onSectionsReordered: (d) => callbacks.onSectionsReordered?.(d as SectionsReorderedEvent),
        onResourceChanged: (d) => callbacks.onResourceChanged?.(d as ResourceChangedEvent),
        onTool: (d) => callbacks.onTool?.(d.name ?? '', d.args),
        onToolResult: (d) =>
          callbacks.onToolResult?.(d.name ?? '', (d.refs ?? []) as import('./editor-chat').DocRef[], d.count, d.levels),
        onRetract: () => callbacks.onRetract?.(),
        onDone: (d) => callbacks.onDone?.(d.text ?? ''),
        onError: (d) => callbacks.onError?.(new Error(d.message ?? 'unknown')),
      });
    } catch { /* ignore malformed frame */ }
  }, undefined, { onSlow: callbacks.onSlow, onActive: callbacks.onActive });
}

function defaultTitleForMode(mode: ChatMode, parcoursId?: string | null): string {
  if (mode === 'voice') return 'Conversation vocale';
  if (mode === 'parcours' && parcoursId) return `Parcours · ${parcoursId}`;
  if (mode === 'cours') return 'Nouveau cours';
  return 'Nouvelle conversation';
}
